import React, { useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  type MRT_Row,
  type MRT_TableOptions,
  type MRT_ColumnFiltersState,
  type MRT_PaginationState,
  type MRT_SortingState,
  useMaterialReactTable,
} from "material-react-table";

import {
  Box,
  Button,
  IconButton,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  useGetSensor,
  useUpdateSensor,
  useDeleteSensors,
  useCreateSensor,
  useDeleteSensor,
  usePollSensors,
  useExportSensors,
} from "../../../hooks/sensors";
import { Sensor } from "../../../types/sensors";
import { toast } from "react-toastify";
import ImportFromCSV from "../../csvimport/importFromCsv";
import { baseURL } from "../../../api/apiClient";

const AdminSensorsTable = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
    []
  );
  const [globalFilter, setGlobalFilter] = useState<string | undefined>("");
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const openImportModal = () => setIsImportModalOpen(true);
  const closeImportModal = () => setIsImportModalOpen(false);

  useEffect(() => {
    setPagination({
      pageIndex: 0,
      pageSize: 10,
    });
  }, [columnFilters]);

  const { mutateAsync: deleteSensor } = useDeleteSensor();
  const exportSensors = useExportSensors(columnFilters, sorting);

  const handleExport = async () => {
    try {
      await exportSensors();
    } catch (error) {
      toast.error("Failed to export");
    } finally {
    }
  };
  const deleteSensors = useDeleteSensors(columnFilters, sorting, rowCount);

  const triggerDelete = async () => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete all currently filtered Sensors?"
    );
    if (confirmDelete) {
      try {
        await deleteSensors();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const {
    data: sensorQueryResults = [],
    isError: isLoadingSensorsError,
    isFetching: isFetchingSensors,
    isLoading: isLoadingSensors,
  } = useGetSensor({ pagination, columnFilters, sorting, globalFilter });

  const fetchedSensors = sensorQueryResults[0]?.sensors || [];

  useEffect(() => {
    const count = sensorQueryResults[0]?.rowCount;
    if (count == 0) {
      setRowCount(0);
    }
    if (!count || count === rowCount) {
      return;
    }
    setRowCount(count);
  }, [sensorQueryResults]);

  const handleSaveSensor: MRT_TableOptions<Sensor>["onEditingRowSave"] =
    async ({ values, table }) => {
      const newValidationErrors = validateSensor(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      setValidationErrors({});
      await updateSensor(values);
    };

  const handleCreateSensor: MRT_TableOptions<Sensor>["onCreatingRowSave"] =
    async ({ values, table }) => {
      const newValidationErrors = validateSensor(values);
      if (Object.values(newValidationErrors).some((error) => error)) {
        setValidationErrors(newValidationErrors);
        return;
      }
      setValidationErrors({});
      await createSensor(values);
    };

  const openDeleteConfirmModal = (row: MRT_Row<Sensor>) => {
    if (window.confirm("Are you sure you want to delete this Sensor?")) {
      deleteSensor(row.original.id);
    }
  };

  function validateSensor(sensor: Sensor) {
    const validateRequired = (value: any) => !!value;

    return {
      name: !validateRequired(sensor.name) ? "Name is required" : "",
    };
  }

  const tableName = "sensors_table";
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const tableState = JSON.parse(localStorage.getItem("tableState") || "{}");
    return (
      tableState[tableName]?.columnVisibility || {
        id: false,
      }
    );
  });

  useEffect(() => {
    localStorage.setItem(
      "tableState",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("tableState") || "{}"),
        [tableName]: {
          columnVisibility,
        },
      })
    );
  }, [columnVisibility, tableName]);

  const columns = useMemo<MRT_ColumnDef<Sensor>[]>(
    () => [
      {
        accessorKey: "id",
        header: "Id",
        enableEditing: false,
        enableSorting: false,
        size: 80,
      },

      {
        accessorKey: "name",
        header: "Name",
        size: 220,
        enableEditing: (row) => !row.original.id,
        Cell: ({ row }) => {
          return <>{row.original.name}</>;
        },
        muiEditTextFieldProps: {
          type: "text",
          required: true,
          error: !!validationErrors?.name,
          helperText: validationErrors?.name,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              name: undefined,
            }),
        },
      },
      {
        accessorKey: "last_seen",
        header: "Last Seen",
        size: 140,
        enableColumnFilter: true,
        enableSorting: true,
        muiEditTextFieldProps: {
          type: "text",
        },
      },

      {
        accessorKey: "tags",
        header: "Tags",
        size: 140,
        enableColumnFilter: true,
        enableSorting: true,
        muiEditTextFieldProps: {
          type: "text",
        },
      },
    ],
    [validationErrors]
  );

  const table = useMaterialReactTable({
    columns,
    enableGlobalFilter: false,
    data: fetchedSensors,
    initialState: {
      showColumnFilters: true,
      density: "compact",
      columnVisibility: columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    selectAllMode: "all",
    enableColumnResizing: true,
    getRowId: (row) => row.id,
    rowNumberDisplayMode: "original",
    muiToolbarAlertBannerProps: isLoadingSensorsError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount,
    muiTableContainerProps: {
      sx: {
        minHeight: "500px",
      },
    },
    renderBottomToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "1rem",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            table.setCreatingRow(true);
          }}
        >
          New Sensor
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            usePollSensors();
          }}
        >
          Poll System
        </Button>
        <Button variant="contained" onClick={handleExport}>
          Export {rowCount} Rows
        </Button>
        <Button variant="contained" onClick={openImportModal}>
          Import
        </Button>
        <Modal
          open={isImportModalOpen}
          onClose={closeImportModal}
          id="modal"
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              height: "90vh",
              width: "90vw",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: "8px",
              overflow: "auto",
            }}
          >
            <Typography id="modal-title" variant="h6" component="h2">
              CSV Import
            </Typography>
            <hr />
            <Typography id="modal-description" sx={{ mt: 2 }}></Typography>
            <ImportFromCSV
              apiUrl={`${baseURL}/api/sensor/import/`}
              importType="sensors"
              onClose={closeImportModal}
            />

            <Button onClick={closeImportModal} sx={{ mt: 2 }}>
              Close
            </Button>
          </Box>
        </Modal>
        <Button variant="contained" color="error" onClick={triggerDelete}>
          Delete {rowCount} Rows
        </Button>
      </Box>
    ),
    onCreatingRowCancel: () => setValidationErrors({}),
    onEditingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateSensor,
    onEditingRowSave: handleSaveSensor,

    enableRowActions: true,
    renderRowActionMenuItems: ({ row, table }) => [
      <Box key={row.id}>
        <Tooltip title="Edit">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>,
    ],

    state: {
      columnFilters,
      globalFilter,
      pagination,
      sorting,
      columnVisibility: columnVisibility,
      isLoading: isLoadingSensors,
      showAlertBanner: isLoadingSensorsError,
      showProgressBars: isFetchingSensors,
    },
  });

  const { mutateAsync: updateSensor } = useUpdateSensor({ table });
  const { mutateAsync: createSensor } = useCreateSensor({ table });

  return <MaterialReactTable table={table} />;
};

export default AdminSensorsTable;
